import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { API_CONFIG } from '../config/api';
import type { FilterOptionsResponse } from '../config/api';
import { fetchRootFolders, fetchFolderContents } from '../logic/groupCreation/enterpriseFoldersApi';
import type { FolderItem } from '../logic/groupCreation/enterpriseFoldersApi';
import { getMyEnterpriseGroups, getArchivedGroups } from '../logic/groupCreation/groupApi';
import '../styles/pages/_consultant-group-workspace.scss';

/* ─────────────────────────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────────────────────────── */
interface WorkspaceCard {
    id: string;
    name: string;
    description: string;
    kind: 'folder' | 'group';
    isProtected: boolean;
    icon: string;
    badge: string;
    badgeMod: string;
    filterBody?: Record<string, string>; // used to fetch count for this card
    membersCount?: number;
    pendingRequests?: number;
}

interface BreadcrumbEntry {
    id: string;
    name: string;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Constants
───────────────────────────────────────────────────────────────────────────── */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const CACHE_KEY = 'enterprise_group_counts_v1';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/* ─────────────────────────────────────────────────────────────────────────────
   API Helpers
───────────────────────────────────────────────────────────────────────────── */
const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
});

async function loadFilterOptions(): Promise<FilterOptionsResponse | null> {
    try {
        const res = await fetch(
            `${API_CONFIG.ENDPOINTS.USERS_ADMIN_FILTER_OPTIONS}?category=student`,
            { headers: getAuthHeaders() }
        );
        if (!res.ok) return null;
        const json = await res.json();
        const filterData = json.success ? (json.data as FilterOptionsResponse) : null;
        
        // Also fetch enterprise cities
        try {
            const citiesRes = await fetch(
                `${API_BASE}/api/groups/enterprise/cities`,
                { headers: getAuthHeaders() }
            );
            if (citiesRes.ok) {
                const citiesJson = await citiesRes.json();
                if (citiesJson.success && filterData) {
                    filterData.cities = citiesJson.data.cities || [];
                }
            }
        } catch (e) {
            // Cities fetch failed, continue with empty cities
        }
        
        return filterData;
    } catch {
        return null;
    }
}

/**
 * Fetch city folders with their real MongoDB IDs for use in card building
 */
async function fetchCityFolders(): Promise<{
    rootFolderId: string;
    cityFolders: Array<{ id: string; name: string; description?: string; groupCount?: number }>
}> {
    try {
        const res = await fetch(
            `${API_BASE}/api/group-folders/enterprise/cities`,
            { headers: getAuthHeaders() }
        );
        if (!res.ok) {
            console.warn(`fetchCityFolders: HTTP ${res.status}`, res.statusText);
            return { rootFolderId: '', cityFolders: [] };
        }
        const json = await res.json();
        const data = json.success ? json.data : { rootFolderId: '', cityFolders: [] };
        
        console.log(`📍 All city folders (raw):`, data.cityFolders);
        console.log(`   Total: ${data.cityFolders.length}, With groups: ${data.cityFolders.filter((f: any) => f.groupCount > 0).length}`);
        
        // Only filter out empty folders for display
        const nonEmptyFolders = data.cityFolders.filter((f: any) => f.groupCount > 0);
        console.log(`✅ Displayed city folders: ${nonEmptyFolders.length}`, nonEmptyFolders);
        
        return { ...data, cityFolders: nonEmptyFolders };
    } catch (err) {
        console.error('❌ Error fetching city folders:', err);
        return { rootFolderId: '', cityFolders: [] };
    }
}

/**
 * Fetch the TOTAL count for a given filter body (limit=1 for efficiency).
 * Returns -1 on error.
 */
async function fetchCountForFilter(body: Record<string, string>): Promise<number> {
    try {
        const res = await fetch(`${API_BASE}/api/groups/enterprise/preview-members`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ ...body, limit: '1', page: '1' }),
        });
        const json = await res.json();
        if (json.success) return json.data.total as number;
        return 0;
    } catch {
        return 0;
    }
}

/* ─────────────────────────────────────────────────────────────────────────────
   SessionStorage Cache
───────────────────────────────────────────────────────────────────────────── */
interface CachePayload {
    counts: Record<string, number>;
    timestamp: number;
}

function readCache(): Record<string, number> | null {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const payload: CachePayload = JSON.parse(raw);
        if (Date.now() - payload.timestamp > CACHE_TTL_MS) {
            sessionStorage.removeItem(CACHE_KEY);
            return null;
        }
        return payload.counts;
    } catch {
        return null;
    }
}

function writeCache(counts: Record<string, number>): void {
    try {
        const payload: CachePayload = { counts, timestamp: Date.now() };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch { /* quota exceeded — ignore */ }
}

function clearCache(): void {
    sessionStorage.removeItem(CACHE_KEY);
}

/* ─────────────────────────────────────────────────────────────────────────────
   Predefined ID helpers
───────────────────────────────────────────────────────────────────────────── */
const isPredefinedId = (id: string | undefined): boolean => {
    if (!id) return false;
    return (
        id === 'predefined_schools' ||
        id === 'predefined_municipalities' ||
        id === 'archived' ||
        id.startsWith('__predefined_') ||
        id.startsWith('__school__') ||
        id.startsWith('__year__') ||
        id.startsWith('__course__') ||
        id.startsWith('__mun__') ||
        id.startsWith('__brgy__')
    );
};

/**
 * Transform breadcrumb from API to handle city folders with friendly names
 * If breadcrumb contains 'cities' folder, replace with 'Municipality' for display
 */
function transformCityBreadcrumb(breadcrumb: BreadcrumbEntry[]): BreadcrumbEntry[] {
    if (!breadcrumb || breadcrumb.length === 0) return breadcrumb;
    return breadcrumb.map(entry => ({
        ...entry,
        name: entry.name === 'cities' ? 'Municipalities' : entry.name,
    }));
}

function predefinedBreadcrumb(id: string): BreadcrumbEntry[] {
    if (id === 'archived') return [{ id, name: 'Archived' }];
    if (id === 'predefined_schools') return [{ id, name: 'Schools' }];
    if (id === 'predefined_municipalities') return [{ id, name: 'Municipalities' }];
    if (id.startsWith('__school__')) {
        const name = id.replace('__school__', '');
        return [{ id: 'predefined_schools', name: 'Schools' }, { id, name }];
    }
    if (id.startsWith('__year__')) {
        const parts = id.replace('__year__', '').split('__');
        return [
            { id: 'predefined_schools', name: 'Schools' },
            { id: `__school__${parts[0]}`, name: parts[0] },
            { id, name: parts[1] },
        ];
    }
    if (id.startsWith('__course__')) {
        const parts = id.replace('__course__', '').split('__');
        return [
            { id: 'predefined_schools', name: 'Schools' },
            { id: `__school__${parts[0]}`, name: parts[0] },
            { id: `__year__${parts[0]}__${parts[1]}`, name: parts[1] },
            { id, name: parts[2] },
        ];
    }
    if (id.startsWith('__mun__')) {
        const name = id.replace('__mun__', '');
        return [{ id: 'predefined_municipalities', name: 'Municipalities' }, { id, name }];
    }
    if (id.startsWith('__brgy__')) {
        const parts = id.replace('__brgy__', '').split('__');
        return [
            { id: 'predefined_municipalities', name: 'Municipalities' },
            { id: `__mun__${parts[0]}`, name: parts[0] },
            { id, name: parts[1] },
        ];
    }
    return [];
}

/* ─────────────────────────────────────────────────────────────────────────────
   Card Builders — every card includes a filterBody for count fetching
───────────────────────────────────────────────────────────────────────────── */
function buildSchoolsCards(opts: FilterOptionsResponse, folderId: string | undefined, counts: Record<string, number>): WorkspaceCard[] {
    if (!folderId) return [{
        id: 'predefined_schools', name: 'Schools', kind: 'folder', isProtected: true,
        description: 'Auto-generated from all registered students',
        icon: 'school', badge: '', badgeMod: 'protected',
    }];

    if (folderId === 'predefined_schools') {
        return opts.schools.map(s => ({
            id: `__school__${s}`, name: s, kind: 'folder', isProtected: true,
            description: `All students enrolled at ${s}`,
            icon: 'account_balance', badge: 'School', badgeMod: 'school',
            filterBody: { category: 'student', school: s },
            membersCount: counts[`__school__${s}`],
        }));
    }

    if (folderId.startsWith('__school__')) {
        const school = folderId.replace('__school__', '');
        return opts.yearLevels.map(y => ({
            id: `__year__${school}__${y}`, name: y, kind: 'folder', isProtected: true,
            description: `Year ${y} students at ${school}`,
            icon: 'calendar_view_month', badge: 'Year Level', badgeMod: 'year',
            filterBody: { category: 'student', school, yearLevel: y },
            membersCount: counts[`__year__${school}__${y}`],
        }));
    }

    if (folderId.startsWith('__year__')) {
        const parts = folderId.replace('__year__', '').split('__');
        const school = parts[0];
        const yearLevel = parts[1];
        return opts.courses.map(c => ({
            id: `__course__${school}__${yearLevel}__${c}`, name: c, kind: 'group', isProtected: true,
            description: `${c} students, Year ${yearLevel} at ${school}`,
            icon: 'menu_book', badge: 'Course', badgeMod: 'course',
            filterBody: { category: 'student', school, yearLevel, course: c },
            membersCount: counts[`__course__${school}__${yearLevel}__${c}`],
            pendingRequests: 0,
        }));
    }

    return [];
}

function buildMunicipalitiesCards(
    opts: FilterOptionsResponse,
    folderId: string | undefined,
    counts: Record<string, number>,
    cityFolders: Array<{ id: string; name: string; description?: string; groupCount?: number }> = []
): WorkspaceCard[] {
    if (!folderId) return [{
        id: 'predefined_municipalities', name: 'Municipalities', kind: 'folder', isProtected: true,
        description: 'Auto-generated from all registered user locations',
        icon: 'location_city', badge: '', badgeMod: 'protected',
    }];

    if (folderId === 'predefined_municipalities') {
        console.log('📍 buildMunicipalitiesCards: cities level');
        console.log('   cityFolders:', cityFolders);
        
        // Build cards with actual city folder IDs from the database
        const cards = cityFolders.length > 0
            ? cityFolders.map(cf => ({
                id: cf.id, // Use real MongoDB folder ID
                name: cf.name,
                kind: 'folder' as const,
                isProtected: true,
                description: cf.description || `Enterprise groups for ${cf.name}`,
                icon: 'holiday_village' as const,
                badge: 'City' as const,
                badgeMod: 'city' as const,
              }))
            : [];
        console.log(`   returning ${cards.length} city cards`);
        return cards;
    }

    // For city folders, don't build any cards (let fetchFolderContents handle it)
    return [];
}

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */
const EnterpriseGroupsPage: React.FC = () => {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const { id: folderId } = useParams<{ id?: string }>();

    // Log when folderId changes
    useEffect(() => {
        console.log('📌 [EnterpriseGroupsPage] FolderId changed to:', folderId);
    }, [folderId]);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterOptions, setFilterOptions] = useState<FilterOptionsResponse | null>(null);
    const [cityFolders, setCityFolders] = useState<Array<{ id: string; name: string; description?: string }>>([]);
    const [citiesRootFolderId, setCitiesRootFolderId] = useState<string>('');
    const [userFolders, setUserFolders] = useState<FolderItem[]>([]);
    const [userGroups, setUserGroups] = useState<any[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [counts, setCounts] = useState<Record<string, number>>(readCache() ?? {});
    const [isFetchingCounts, setIsFetchingCounts] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<{ id: string; name: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [showBulkRestoreModal, setShowBulkRestoreModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [groupToRestore, setGroupToRestore] = useState<{ id: string; name: string } | null>(null);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const fetchingRef = useRef(false); // prevents double-fetch

    /* ── Load page data ──────────────────────────────────────────────────────── */
    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            console.log('🔄 [loadData] Starting... folderId:', folderId);
            
            // Always clear old data immediately at the start to prevent flashing stale content
            // isLoading is true, so UI will show loading spinner instead of empty
            setUserFolders([]);
            setUserGroups([]);
            setBreadcrumb([]);
            
            // Clear selected groups when navigating to different pages
            if (folderId !== 'archived') {
                setSelectedGroups(new Set());
                setIsSelectionMode(false);
            }
            
            const opts = await loadFilterOptions();
            setFilterOptions(opts);
            
            // Also fetch city folders for building cards with real IDs
            const cityData = await fetchCityFolders();
            setCityFolders(cityData.cityFolders);
            setCitiesRootFolderId(cityData.rootFolderId);

            if (folderId === 'archived') {
                console.log('🔄 [loadData] Branch: folderId === archived');
                // Load archived groups
                setBreadcrumb([{ id: 'archived', name: 'Archived' }]);
                
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        console.log('📦 [loadData] Fetching archived groups...');
                        const response = await getArchivedGroups(token, 1, 1000);
                        console.log('📦 [loadData] Archive response:', response);
                        if (response.success) {
                            console.log('✅ [loadData] Setting', response.data.groups.length, 'archived groups');
                            setUserGroups(response.data.groups);
                        } else {
                            console.error('❌ [loadData] Archive response was not successful:', response);
                            setUserGroups([]);
                        }
                    }
                } catch (error) {
                    console.error('❌ Failed to fetch archived groups:', error);
                    setUserGroups([]);
                }
            } else if (isPredefinedId(folderId)) {
                console.log('🔄 [loadData] Branch: isPredefinedId');
                setBreadcrumb(predefinedBreadcrumb(folderId!));
                setUserFolders([]);
                setUserGroups([]);
            } else if (!folderId) {
                console.log('🔄 [loadData] Branch: root (no folderId)');
                // Load root folders and enterprise groups
                const rootFolders = await fetchRootFolders().catch(() => []);
                setUserFolders(rootFolders);
                
                // Fetch root-level groups created by enterprise (excluding groups in folders)
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        const response = await getMyEnterpriseGroups(token, 1, 1000);
                        if (response.success) {
                            // Filter to only show groups that don't have a folderId (root-level groups)
                            const rootLevelGroups = response.data.groups.filter(group => !group.folderId);
                            setUserGroups(rootLevelGroups);
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch enterprise groups:', error);
                    setUserGroups([]);
                }
            } else {
                console.log('🔄 [loadData] Branch: folder contents for folderId:', folderId);
                const contents = await fetchFolderContents(folderId).catch(() => null);
                if (contents) {
                    setUserFolders(contents.childFolders);
                    setUserGroups(contents.childGroups);
                    // Transform breadcrumb to show friendly names for city folders
                    const transformedBreadcrumb = transformCityBreadcrumb(contents.breadcrumb);
                    setBreadcrumb(transformedBreadcrumb);
                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [folderId]);

    useEffect(() => { loadData(); }, [loadData]);

    /* ── Fetch counts for current predefined level (cached) ─────────────────── */
    const fetchCurrentLevelCounts = useCallback(async (opts: FilterOptionsResponse, force = false) => {
        if (!isPredefinedId(folderId)) return;
        if (fetchingRef.current) return;

        // Build list of cards that need counts at this level
        const cards = [
            ...buildSchoolsCards(opts, folderId, {}),
            ...buildMunicipalitiesCards(opts, folderId, {}, cityFolders),
        ].filter(c => c.filterBody);

        if (cards.length === 0) return;

        // Check if all are already cached
        const cached = readCache() ?? {};
        const uncached = cards.filter(c => force || cached[c.id] === undefined);
        if (uncached.length === 0) {
            setCounts(cached);
            return;
        }

        fetchingRef.current = true;
        setIsFetchingCounts(true);

        // Fetch all counts in parallel for this level
        const newCounts = { ...cached };
        await Promise.all(
            uncached.map(async card => {
                const total = await fetchCountForFilter(card.filterBody!);
                newCounts[card.id] = total;
            })
        );

        writeCache(newCounts);
        setCounts(newCounts);
        fetchingRef.current = false;
        setIsFetchingCounts(false);
    }, [folderId]);

    useEffect(() => {
        if (!isLoading && filterOptions && isPredefinedId(folderId)) {
            fetchCurrentLevelCounts(filterOptions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, folderId]);

    /* ── Refresh handler ─────────────────────────────────────────────────────── */
    const handleRefresh = useCallback(() => {
        clearCache();
        setCounts({});
        if (filterOptions && isPredefinedId(folderId)) {
            fetchCurrentLevelCounts(filterOptions, true);
        } else {
            loadData();
        }
    }, [filterOptions, folderId, fetchCurrentLevelCounts, loadData]);

    /* ── Archive handler ─────────────────────────────────────────────────────── */
    const handleArchive = useCallback(async (groupId: string) => {
        try {
            console.log('⏳  [handleArchive] Starting archive for group:', groupId);
            const response = await fetch(`${API_BASE}/api/groups/${groupId}/archive`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
            });

            console.log('📊 [handleArchive] Response status:', response.status);
            const json = await response.json();
            console.log('📊 [handleArchive] Response:', json);
            
            if (json.success) {
                console.log('✅ [handleArchive] Archive successful, navigating to archived folder');
                clearCache(); // Clear any cached data
                // Navigate to archived folder - useEffect will automatically call loadData when folderId changes
                navigate('/panel/enterprise/groups/archived');
            } else {
                console.error('❌ [handleArchive] Archive failed:', json.message);
                alert('Failed to archive group: ' + (json.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('❌ [handleArchive] Error:', error);
            alert('Error archiving group. Please try again.');
        }
    }, [navigate, clearCache]);

    /* ── Restore handler ─────────────────────────────────────────────────────── */
    const handleRestoreClick = useCallback((groupId: string, groupName: string) => {
        setGroupToRestore({ id: groupId, name: groupName });
        setShowRestoreModal(true);
    }, []);

    const handleRestore = useCallback(async () => {
        if (!groupToRestore) return;

        setIsRestoring(true);
        try {
            console.log('⏳ [handleRestore] Starting restore for group:', groupToRestore.id);
            const response = await fetch(`${API_BASE}/api/groups/${groupToRestore.id}/restore`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
            });

            const json = await response.json();
            console.log('📊 [handleRestore] Response:', json);
            
            if (json.success) {
                console.log('✅ [handleRestore] Restore successful');
                setShowRestoreModal(false);
                setGroupToRestore(null);
                clearCache();
                loadData();
            } else {
                alert('Failed to restore group: ' + (json.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('❌ [handleRestore] Error:', error);
            alert('Error restoring group. Please try again.');
        } finally {
            setIsRestoring(false);
        }
    }, [groupToRestore, loadData]);

    /* ── Delete handler ──────────────────────────────────────────────────────── */
    const handleDeleteClick = useCallback((groupId: string, groupName: string) => {
        setGroupToDelete({ id: groupId, name: groupName });
        setShowDeleteModal(true);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!groupToDelete) return;

        setIsDeleting(true);
        try {
            console.log('⏳  [handleDelete] Deleting group:', groupToDelete.id);
            const response = await fetch(`${API_BASE}/api/groups/${groupToDelete.id}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            const json = await response.json();
            console.log('📊 [handleDelete] Response:', json);
            
            if (json.success) {
                console.log('✅ [handleDelete] Delete successful');
                setShowDeleteModal(false);
                setGroupToDelete(null);
                clearCache();
                loadData();
            } else {
                alert('Failed to delete group: ' + (json.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('❌ [handleDelete] Error:', error);
            alert('Error deleting group. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    }, [groupToDelete, loadData]);

    /* ── Multi-select handlers ────────────────────────────────────────────────── */
    const toggleSelectGroup = useCallback((groupId: string) => {
        setSelectedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    }, []);

    const selectAllArchivedGroups = useCallback(() => {
        if (selectedGroups.size === userGroups.filter(g => g._id || g.id).length && selectedGroups.size > 0) {
            // Deselect all
            setSelectedGroups(new Set());
        } else {
            // Select all
            setSelectedGroups(new Set(userGroups.map(g => String(g._id || g.id))));
        }
    }, [userGroups, selectedGroups.size]);

    const exitSelectionMode = useCallback(() => {
        setIsSelectionMode(false);
        setSelectedGroups(new Set());
    }, []);

    const handleBulkRestoreClick = useCallback(() => {
        if (selectedGroups.size > 0) {
            setShowBulkRestoreModal(true);
        }
    }, [selectedGroups.size]);

    const handleBulkDeleteClick = useCallback(() => {
        if (selectedGroups.size > 0) {
            setShowBulkDeleteModal(true);
        }
    }, [selectedGroups.size]);

    const handleBulkRestore = useCallback(async () => {
        setIsBulkProcessing(true);
        try {
            console.log('⏳ [handleBulkRestore] Restoring', selectedGroups.size, 'groups');
            let successCount = 0;
            let failCount = 0;

            for (const groupId of Array.from(selectedGroups)) {
                try {
                    const response = await fetch(`${API_BASE}/api/groups/${groupId}/restore`, {
                        method: 'PATCH',
                        headers: getAuthHeaders(),
                    });
                    const json = await response.json();
                    if (json.success) {
                        successCount++;
                    } else {
                        failCount++;
                    }
                } catch (error) {
                    console.error('❌ Error restoring group:', groupId, error);
                    failCount++;
                }
            }

            console.log('✅ Bulk restore complete: ', successCount, 'successful,', failCount, 'failed');
            setShowBulkRestoreModal(false);
            setSelectedGroups(new Set());
            setIsSelectionMode(false);
            clearCache();
            loadData();
            
            if (failCount > 0) {
                alert(`Restored ${successCount} group(s). ${failCount} failed.`);
            }
        } catch (error) {
            console.error('❌ Error in bulk restore:', error);
            alert('Error restoring groups. Please try again.');
        } finally {
            setIsBulkProcessing(false);
        }
    }, [selectedGroups, loadData]);

    const handleBulkDelete = useCallback(async () => {
        setIsBulkProcessing(true);
        try {
            console.log('⏳ [handleBulkDelete] Deleting', selectedGroups.size, 'groups');
            let successCount = 0;
            let failCount = 0;

            for (const groupId of Array.from(selectedGroups)) {
                try {
                    const response = await fetch(`${API_BASE}/api/groups/${groupId}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                    });
                    const json = await response.json();
                    if (json.success) {
                        successCount++;
                    } else {
                        failCount++;
                    }
                } catch (error) {
                    console.error('❌ Error deleting group:', groupId, error);
                    failCount++;
                }
            }

            console.log('✅ Bulk delete complete:', successCount, 'successful,', failCount, 'failed');
            setShowBulkDeleteModal(false);
            setSelectedGroups(new Set());
            setIsSelectionMode(false);
            clearCache();
            loadData();
            
            if (failCount > 0) {
                alert(`Deleted ${successCount} group(s). ${failCount} failed.`);
            }
        } catch (error) {
            console.error('❌ Error in bulk delete:', error);
            alert('Error deleting groups. Please try again.');
        } finally {
            setIsBulkProcessing(false);
        }
    }, [selectedGroups, loadData]);

    /* ── Build cards ─────────────────────────────────────────────────────────── */
    const predefinedCards: WorkspaceCard[] = filterOptions
        ? [
            ...(folderId?.startsWith('__school__') || folderId?.startsWith('__year__') || folderId?.startsWith('__course__') || folderId === 'predefined_schools' 
                ? buildSchoolsCards(filterOptions, folderId, counts) 
                : []),
            ...(folderId?.startsWith('__mun__') || folderId?.startsWith('__brgy__') || folderId === 'predefined_municipalities' 
                ? buildMunicipalitiesCards(filterOptions, folderId, counts, cityFolders) 
                : []),
            // Only add root cards when at root level
            ...(!folderId ? [...buildSchoolsCards(filterOptions, undefined, counts), ...buildMunicipalitiesCards(filterOptions, undefined, counts, cityFolders)] : []),
        ]
        : [];

    const userCards: WorkspaceCard[] = (isPredefinedId(folderId) && folderId !== 'archived') ? [] : [
        ...(!folderId ? [{
            id: 'archived', name: 'Archived', description: 'View archived items',
            kind: 'folder' as const, isProtected: false, icon: 'archive', badge: 'Archived', badgeMod: 'archived',
        }] : []),
        ...userFolders
            .filter(f => f.name !== 'cities' && f.name !== 'protected_cities' && f.name !== 'Geographic' && f.name !== 'GLOBAL') // Hide infrastructure folders
            .map(f => {
            // Mark cities folder and its subfolders as protected
            const isCitiesFolder = f.name === 'cities';
            const isSubfolderOfCities = Boolean(citiesRootFolderId && f.parentFolderId === citiesRootFolderId);
            const isProtected: boolean = isCitiesFolder || isSubfolderOfCities;
            return {
                id: f.id, 
                name: f.name, 
                description: f.description || '',
                kind: 'folder' as const, 
                isProtected: isProtected, 
                icon: isProtected ? 'location_city' : 'corporate_fare',
                badge: isProtected ? 'Protected' : 'Folder', 
                badgeMod: isProtected ? 'protected' : 'folder',
            };
        }),
        ...userGroups.map((g: any) => ({
            id: String(g._id || g.id), 
            name: String(g.groupName || g.name), 
            description: String(g.description || ''),
            kind: 'group' as const, 
            isProtected: false, 
            icon: 'groups',
            badge: String(g.groupType || 'Group'), 
            badgeMod: 'default',
            membersCount: Number(g.totalMembers ?? g.membersCount ?? 0),
            pendingRequests: Number(g.pendingRequests ?? 0),
        })),
    ];

    const allCards = [...predefinedCards, ...userCards];
    const filteredCards = allCards.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const groupsWithPending = userCards.filter(c => c.kind === 'group' && (c.pendingRequests ?? 0) > 0);
    const totalPending = groupsWithPending.reduce((n, c) => n + (c.pendingRequests ?? 0), 0);

    const navigateBack = () => {
        if (folderId === 'archived') {
            // Archive page always goes back to root
            navigate('/panel/enterprise/groups');
        } else if (breadcrumb.length > 1) {
            const parentId = breadcrumb[breadcrumb.length - 2].id;
            // If parent is a city folder (breadcrumb shows 'Municipality'), navigate to predefined Cities page
            const parentName = breadcrumb[breadcrumb.length - 2].name;
            if (parentName === 'Municipalities' && breadcrumb.length === 2) {
                navigate('/panel/enterprise/groups/predefined_municipalities');
            } else {
                navigate(`/panel/enterprise/groups/${parentId}`);
            }
        } else {
            navigate('/panel/enterprise/groups');
        }
    };

    const currentTitle = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].name : 'Group Workspace';

    /* ── Render ──────────────────────────────────────────────────────────────── */
    return (
        <div className={`consultant-group-workspace ${isDarkMode ? 'dark' : ''}`}>

            {/* ── Top Bar ── */}
            <header className="consultant-group-workspace__header">
                <div className="consultant-group-workspace__header-content">
                    <div className="consultant-group-workspace__breadcrumbs">
                        <button className="consultant-group-workspace__breadcrumb-link consultant-group-workspace__breadcrumb-link--button"
                            onClick={() => navigate('/panel/enterprise/groups')}>
                            <span className="material-symbols-outlined consultant-group-workspace__breadcrumb-icon">corporate_fare</span>
                            Enterprise
                        </button>
                        <span className="consultant-group-workspace__breadcrumb-separator">/</span>
                        <button className="consultant-group-workspace__breadcrumb-link consultant-group-workspace__breadcrumb-link--button"
                            onClick={() => navigate('/panel/enterprise/groups')}>
                            Groups
                        </button>
                        {breadcrumb.map((entry, i) => (
                            <React.Fragment key={entry.id}>
                                <span className="consultant-group-workspace__breadcrumb-separator">/</span>
                                {i === breadcrumb.length - 1
                                    ? <span className="consultant-group-workspace__breadcrumb-current">{entry.name}</span>
                                    : <button className="consultant-group-workspace__breadcrumb-link consultant-group-workspace__breadcrumb-link--button"
                                        onClick={() => navigate(`/panel/enterprise/groups/${entry.id}`)}>
                                        {entry.name}
                                    </button>}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="consultant-group-workspace__pending-summary">
                        {totalPending > 0 && (
                            <button className="consultant-group-workspace__pending-info"
                                onClick={() => navigate(`/panel/enterprise/groups/view/${groupsWithPending[0].id}`)}>
                                <span className="material-symbols-outlined">mail</span>
                                <span>{totalPending} Pending Request{totalPending !== 1 ? 's' : ''}</span>
                                <span className="consultant-group-workspace__pending-separator">•</span>
                                <span className="consultant-group-workspace__pending-groups-inline">
                                    {groupsWithPending.slice(0, 3).map(g => g.name).join(', ')}
                                    {groupsWithPending.length > 3 ? ` +${groupsWithPending.length - 3} more` : ''}
                                </span>
                            </button>
                        )}
                    </div>

                    <div className="consultant-group-workspace__actions">
                        <div className="consultant-group-workspace__search">
                            <span className="material-symbols-outlined consultant-group-workspace__search-icon">search</span>
                            <input type="text" className="consultant-group-workspace__search-input"
                                placeholder={folderId ? 'Search in this folder…' : 'Search groups and folders…'}
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        </div>
                        {folderId === 'archived' && isSelectionMode && (
                            <Button className="consultant-group-workspace__add-btn"
                                onClick={exitSelectionMode}
                                style={{
                                    backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                                    color: isDarkMode ? '#cbd5e1' : '#334155',
                                    border: `1.5px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
                                    fontWeight: '500',
                                }}>
                                <span className="material-symbols-outlined">close</span>
                                <span>Cancel</span>
                            </Button>
                        )}
                        {folderId === 'archived' && !isSelectionMode && userGroups.length > 0 && (
                            <Button className="consultant-group-workspace__add-btn"
                                onClick={() => setIsSelectionMode(true)}
                                style={{
                                    backgroundColor: isDarkMode ? '#0d9488' : '#14b8a6',
                                    color: '#fff',
                                    border: 'none',
                                    fontWeight: '600',
                                }}>
                                <span className="material-symbols-outlined">check_circle</span>
                                <span>Select</span>
                            </Button>
                        )}
                        <Button className="consultant-group-workspace__add-btn"
                            onClick={() => navigate('/panel/enterprise/group-creation')}
                            disabled={isPredefinedId(folderId) || isSelectionMode}>
                            <span className="material-symbols-outlined">add</span>
                            <span>New Group</span>
                        </Button>
                        <button className="consultant-group-workspace__settings-btn">
                            <span className="material-symbols-outlined">settings</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* ── Main ── */}
            <main className="consultant-group-workspace__main">
                <div className="consultant-group-workspace__content">
                    <div className="consultant-group-workspace__content-header">
                        <h2 className="consultant-group-workspace__title">{currentTitle}</h2>
                        <p className="consultant-group-workspace__subtitle">
                            {isPredefinedId(folderId)
                                ? 'This folder is automatically generated from user data and is protected.'
                                : folderId
                                    ? 'Manage folders and groups within this workspace.'
                                    : 'Browse predefined group hierarchies or manage your custom folders below.'}
                        </p>
                        {!!folderId && (
                            <button className="consultant-group-workspace__back-btn" onClick={navigateBack}>
                                <span className="material-symbols-outlined">arrow_back</span>
                                Back
                            </button>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="consultant-group-workspace__loading">
                            <span className="material-symbols-outlined consultant-group-workspace__loading-icon">sync</span>
                            <p>Loading workspace…</p>
                        </div>
                    ) : (
                        <>
                            {/* Selection toolbar for archived groups */}
                            {folderId === 'archived' && isSelectionMode && userGroups.length > 0 && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '20px',
                                    padding: '16px 20px',
                                    marginBottom: '24px',
                                    backgroundColor: isDarkMode 
                                        ? '#0f172a' 
                                        : '#f0fdf9',
                                    borderRadius: '12px',
                                    border: `1.5px solid ${isDarkMode ? 'rgba(13, 148, 136, 0.35)' : 'rgba(20, 184, 166, 0.25)'}`,
                                    boxShadow: isDarkMode 
                                        ? '0 4px 16px rgba(0, 0, 0, 0.4)' 
                                        : '0 2px 8px rgba(13, 148, 136, 0.08)',
                                }}>
                                    <label style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px', 
                                        cursor: 'pointer', 
                                        flexShrink: 0,
                                        userSelect: 'none',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedGroups.size === userGroups.length && userGroups.length > 0}
                                            onChange={selectAllArchivedGroups}
                                            style={{ 
                                                width: '22px', 
                                                height: '22px', 
                                                cursor: 'pointer',
                                                accentColor: '#14b8a6',
                                            }}
                                        />
                                        <span style={{ 
                                            fontSize: '15px', 
                                            fontWeight: '600',
                                            color: isDarkMode ? '#f1f5f9' : '#0f172a',
                                        }}>
                                            Select All
                                        </span>
                                    </label>

                                    <span style={{ 
                                        fontSize: '15px', 
                                        fontWeight: '700',
                                        color: isDarkMode ? '#14b8a6' : '#0d9488',
                                        minWidth: '100px',
                                    }}>
                                        {selectedGroups.size} selected
                                    </span>
                                    
                                    {selectedGroups.size > 0 && (
                                        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                                            <button
                                                onClick={handleBulkRestoreClick}
                                                disabled={isBulkProcessing}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '10px 18px',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    backgroundColor: isDarkMode 
                                                        ? '#0d9488' 
                                                        : '#14b8a6',
                                                    color: '#fff',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: isBulkProcessing ? 'not-allowed' : 'pointer',
                                                    opacity: isBulkProcessing ? 0.65 : 1,
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: isBulkProcessing 
                                                        ? 'none' 
                                                        : isDarkMode
                                                            ? '0 4px 12px rgba(13, 148, 136, 0.3)'
                                                            : '0 4px 12px rgba(20, 184, 166, 0.3)',
                                                    transform: isBulkProcessing ? 'scale(0.98)' : 'scale(1)',
                                                }}
                                                onMouseEnter={e => {
                                                    if (!isBulkProcessing) {
                                                        const btn = e.currentTarget as HTMLButtonElement;
                                                        btn.style.boxShadow = isDarkMode
                                                            ? '0 6px 20px rgba(13, 148, 136, 0.4)'
                                                            : '0 6px 20px rgba(20, 184, 166, 0.4)';
                                                        btn.style.transform = 'translateY(-2px)';
                                                        btn.style.backgroundColor = isDarkMode ? '#0f766e' : '#06b6d4';
                                                    }
                                                }}
                                                onMouseLeave={e => {
                                                    const btn = e.currentTarget as HTMLButtonElement;
                                                    btn.style.boxShadow = isDarkMode
                                                        ? '0 4px 12px rgba(13, 148, 136, 0.3)'
                                                        : '0 4px 12px rgba(20, 184, 166, 0.3)';
                                                    btn.style.transform = 'translateY(0)';
                                                    btn.style.backgroundColor = isDarkMode ? '#0d9488' : '#14b8a6';
                                                }}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                                                    {isBulkProcessing ? 'sync' : 'restore'}
                                                </span>
                                                <span>Restore</span>
                                            </button>
                                            <button
                                                onClick={handleBulkDeleteClick}
                                                disabled={isBulkProcessing}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '10px 18px',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    backgroundColor: isDarkMode 
                                                        ? '#7f1d1d' 
                                                        : '#dc2626',
                                                    color: '#fff',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: isBulkProcessing ? 'not-allowed' : 'pointer',
                                                    opacity: isBulkProcessing ? 0.65 : 1,
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: isBulkProcessing 
                                                        ? 'none' 
                                                        : isDarkMode
                                                            ? '0 4px 12px rgba(127, 29, 29, 0.3)'
                                                            : '0 4px 12px rgba(220, 38, 38, 0.3)',
                                                    transform: isBulkProcessing ? 'scale(0.98)' : 'scale(1)',
                                                }}
                                                onMouseEnter={e => {
                                                    if (!isBulkProcessing) {
                                                        const btn = e.currentTarget as HTMLButtonElement;
                                                        btn.style.boxShadow = isDarkMode
                                                            ? '0 6px 20px rgba(127, 29, 29, 0.4)'
                                                            : '0 6px 20px rgba(220, 38, 38, 0.4)';
                                                        btn.style.transform = 'translateY(-2px)';
                                                        btn.style.backgroundColor = isDarkMode ? '#991b1b' : '#b91c1c';
                                                    }
                                                }}
                                                onMouseLeave={e => {
                                                    const btn = e.currentTarget as HTMLButtonElement;
                                                    btn.style.boxShadow = isDarkMode
                                                        ? '0 4px 12px rgba(127, 29, 29, 0.3)'
                                                        : '0 4px 12px rgba(220, 38, 38, 0.3)';
                                                    btn.style.transform = 'translateY(0)';
                                                    btn.style.backgroundColor = isDarkMode ? '#7f1d1d' : '#dc2626';
                                                }}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                                                    {isBulkProcessing ? 'sync' : 'delete_sweep'}
                                                </span>
                                                <span>Delete</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Main grid */}
                            <div className="consultant-group-workspace__grid" key={folderId || 'root'}>
                            {filteredCards.map(card => {
                                const count = counts[card.id] ?? card.membersCount ?? 0;
                                const countPending = card.pendingRequests ?? 0;
                                const countUnknown = isFetchingCounts && card.isProtected && counts[card.id] === undefined;

                                return (
                                    <div
                                        key={card.id}
                                        className={[
                                            'consultant-group-workspace__card',
                                            card.kind === 'folder' ? 'consultant-group-workspace__card--folder' : '',
                                            card.isProtected ? 'consultant-group-workspace__card--protected' : '',
                                        ].filter(Boolean).join(' ')}
                                        onClick={card.kind === 'folder' ? () => navigate(`/panel/enterprise/groups/${card.id}`) : undefined}
                                        style={{ cursor: card.kind === 'folder' ? 'pointer' : 'default' }}
                                    >
                                        {/* Header */}
                                        <div className="consultant-group-workspace__card-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                                {/* Checkbox for archived groups */}
                                                {folderId === 'archived' && isSelectionMode && card.kind === 'group' && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedGroups.has(card.id)}
                                                        onChange={() => toggleSelectGroup(card.id)}
                                                        onClick={e => e.stopPropagation()}
                                                        style={{
                                                            width: '22px',
                                                            height: '22px',
                                                            cursor: 'pointer',
                                                            flexShrink: 0,
                                                            accentColor: '#14b8a6',
                                                        }}
                                                    />
                                                )}
                                                <div className="consultant-group-workspace__card-icon"
                                                    style={card.isProtected ? { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } : undefined}>
                                                    <span className="material-symbols-outlined">{card.icon}</span>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                                {card.isProtected && (
                                                    <span className="consultant-group-workspace__card-badge consultant-group-workspace__card-badge--protected"
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}
                                                        title="Auto-generated and protected from deletion">
                                                        <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>lock</span>
                                                        Protected
                                                    </span>
                                                )}
                                                <span className={`consultant-group-workspace__card-badge consultant-group-workspace__card-badge--${card.badgeMod}`}>
                                                    {card.badge}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Body */}
                                        <div className="consultant-group-workspace__card-body">
                                            <h3 className="consultant-group-workspace__card-title" title={card.name}>{card.name}</h3>
                                            <p className="consultant-group-workspace__card-desc" title={card.description}>
                                                {card.description || 'No description available.'}
                                            </p>
                                        </div>

                                        {/* Footer */}
                                        <div className="consultant-group-workspace__card-footer">
                                            <div className="consultant-group-workspace__card-meta">
                                                <span className="material-symbols-outlined">
                                                    {card.kind === 'folder' ? 'folder_open' : 'groups'}
                                                </span>
                                                <span>
                                                    {countUnknown
                                                        ? '…'
                                                        : card.kind === 'folder'
                                                            ? 'Folder'
                                                            : `${count} Members • ${countPending} Requests`}
                                                </span>
                                            </div>

                                            {!card.isProtected && card.id !== 'archived' && (
                                                <div className="consultant-group-workspace__card-actions">
                                                    {folderId === 'archived' ? (
                                                        <>
                                                            <button className="consultant-group-workspace__action-btn consultant-group-workspace__action-btn--primary"
                                                                onClick={e => { e.stopPropagation(); handleRestoreClick(card.id, card.name); }}
                                                                title="Restore this group">
                                                                <span className="material-symbols-outlined">restore</span>
                                                                <span>Restore</span>
                                                            </button>
                                                            <button className="consultant-group-workspace__action-btn consultant-group-workspace__action-btn--danger"
                                                                onClick={e => { e.stopPropagation(); handleDeleteClick(card.id, card.name); }}
                                                                title="Permanently delete this group">
                                                                <span className="material-symbols-outlined">delete</span>
                                                                <span>Delete</span>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {card.kind === 'group' && (
                                                                <button className="consultant-group-workspace__action-btn consultant-group-workspace__action-btn--primary"
                                                                    onClick={e => { e.stopPropagation(); navigate(`/panel/enterprise/groups/view/${card.id}`); }}
                                                                    title="View and manage group">
                                                                    <span className="material-symbols-outlined">visibility</span>
                                                                    <span>View</span>
                                                                </button>
                                                            )}
                                                            <button className="consultant-group-workspace__action-btn consultant-group-workspace__action-btn--secondary"
                                                                onClick={e => { e.stopPropagation(); navigate(`/panel/enterprise/groups/edit/${card.id}`); }}
                                                                title="Edit group settings">
                                                                <span className="material-symbols-outlined">edit</span>
                                                                <span>Edit</span>
                                                            </button>
                                                            <button className="consultant-group-workspace__action-btn consultant-group-workspace__action-btn--danger"
                                                                onClick={e => { e.stopPropagation(); handleArchive(card.id); }} 
                                                                title="Archive this group">
                                                                <span className="material-symbols-outlined">archive</span>
                                                                <span>Archive</span>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {filteredCards.length === 0 && (
                                <div className="consultant-group-workspace__empty-state">
                                    <div className="consultant-group-workspace__empty-icon">
                                        <span className="material-symbols-outlined">search_off</span>
                                    </div>
                                    <h3>{searchTerm ? 'No results found' : 'This folder is empty'}</h3>
                                    <p>
                                        {searchTerm
                                            ? `No items match "${searchTerm}".`
                                            : 'Add a new folder or group to get started.'}
                                    </p>
                                </div>
                            )}
                        </div>
                        </>
                    )}
                </div>
            </main>

            {/* ── Floating Refresh Button ── */}
            <button
                className="consultant-group-workspace__fab"
                onClick={handleRefresh}
                disabled={isFetchingCounts || isLoading}
                title={isFetchingCounts ? 'Loading member counts…' : 'Refresh member counts'}
                style={{
                    position: 'fixed',
                    bottom: '32px',
                    right: '32px',
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    border: 'none',
                    background: isFetchingCounts
                        ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isFetchingCounts ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.45)',
                    zIndex: 999,
                    transition: 'all 0.2s',
                }}
            >
                <span
                    className="material-symbols-outlined"
                    style={{
                        fontSize: '24px',
                        animation: isFetchingCounts ? 'spin 1s linear infinite' : 'none',
                    }}
                >
                    {isFetchingCounts ? 'sync' : 'refresh'}
                </span>
            </button>

            {/* Delete confirmation modal */}
            <Modal
                isOpen={showDeleteModal}
                title="Delete Group"
                onClose={() => {
                    setShowDeleteModal(false);
                    setGroupToDelete(null);
                }}
                actionLabel="Delete"
                onAction={handleDelete}
                actionDisabled={isDeleting}
                isDanger={true}
                closeLabel="Cancel"
            >
                <p>Are you sure you want to permanently delete <strong>{groupToDelete?.name}</strong>?</p>
                <p style={{ marginTop: '12px', fontSize: '14px', opacity: 0.8 }}>
                    This action cannot be undone. All group data will be permanently removed.
                </p>
            </Modal>

            {/* Restore confirmation modal */}
            <Modal
                isOpen={showRestoreModal}
                title="Restore Group"
                onClose={() => {
                    setShowRestoreModal(false);
                    setGroupToRestore(null);
                }}
                actionLabel="Restore"
                onAction={handleRestore}
                actionDisabled={isRestoring}
                isDanger={false}
                closeLabel="Cancel"
            >
                <p>Are you sure you want to restore <strong>{groupToRestore?.name}</strong>?</p>
                <p style={{ marginTop: '12px', fontSize: '14px', opacity: 0.8 }}>
                    This group will be restored and moved back to its original location.
                </p>
            </Modal>

            {/* Bulk restore confirmation modal */}
            <Modal
                isOpen={showBulkRestoreModal}
                title="Restore Groups"
                onClose={() => setShowBulkRestoreModal(false)}
                actionLabel="Restore"
                onAction={handleBulkRestore}
                actionDisabled={isBulkProcessing}
                isDanger={false}
                closeLabel="Cancel"
            >
                <p>Are you sure you want to restore <strong>{selectedGroups.size} group{selectedGroups.size !== 1 ? 's' : ''}</strong>?</p>
                <p style={{ marginTop: '12px', fontSize: '14px', opacity: 0.8 }}>
                    All selected groups will be restored and moved back to their original locations.
                </p>
            </Modal>

            {/* Bulk delete confirmation modal */}
            <Modal
                isOpen={showBulkDeleteModal}
                title="Delete Groups"
                onClose={() => setShowBulkDeleteModal(false)}
                actionLabel="Delete"
                onAction={handleBulkDelete}
                actionDisabled={isBulkProcessing}
                isDanger={true}
                closeLabel="Cancel"
            >
                <p>Are you sure you want to permanently delete <strong>{selectedGroups.size} group{selectedGroups.size !== 1 ? 's' : ''}</strong>?</p>
                <p style={{ marginTop: '12px', fontSize: '14px', opacity: 0.8 }}>
                    This action cannot be undone. All group data will be permanently removed.
                </p>
            </Modal>

            {/* Spin keyframe */}
            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default EnterpriseGroupsPage;
